import { useMemo, useState } from "react";
import { Plus, CalendarOff, Route } from "lucide-react";
import { Button, Card, CardEmpty, CardEmptyAction, DarkHero, EmptyState, Icon, Modal, Page, Skeleton, SplitButton, VenueCntrlWordmark } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useShows, useTours, useTourDays, type ShowFilters, type TourDayDetail } from "../../app/hooks/useShows.js";
import { useShowMoney, showMoneyById } from "../../app/hooks/useMoney.js";
import { useBandTeam } from "../../app/hooks/useBandTeam.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { SyncMerchButton } from "./SyncMerchButton.js";
import { BandsintownExportModal } from "./BandsintownExportModal.js";
import { BookingKpiStrip } from "./BookingKpiStrip.js";
import { OutstandingPaymentsCard } from "./OutstandingPaymentsCard.js";
import { BookingTimeline } from "./BookingRows.js";
import { PendingInvitesGroup } from "./PendingInvitesGroup.js";
import { TourBuilder } from "./TourBuilder.js";
import { OffDayModal } from "./OffDayModal.js";
import { GuestShowModal } from "./GuestShowModal.js";
import { useSharedShows } from "../../app/hooks/useShowShares.js";
import { buildTimeline, todayIso, type BookingTab } from "./lib/bookingRows.js";
import styles from "./BookingScreen.module.scss";

export interface BookingScreenProps {
  onAddShow: () => void;
  onOpenShow: (showId: string) => void;
  /** Open the show EDITOR — the Holds tab's row action (TASK-057). Both are routed modals over this page. */
  onEditShow: (showId: string) => void;
  /** The active tab, from `?tab`. URL-driven so a confirm can land the user on Confirmed. */
  tab: BookingTab;
  /** Report a tab click back to the URL — the param is the single source of truth, not local state. */
  onTabChange: (tab: BookingTab) => void;
}

const TABS: { key: BookingTab; label: string }[] = [
  { key: "confirmed", label: "Confirmed" },
  { key: "holds", label: "Holds" },
  { key: "past", label: "Past" },
];

/** The shows card's title CHANGES with the tab — it is the page's only visible heading. */
const CARD_TITLE: Record<BookingTab, string> = {
  confirmed: "Upcoming Shows",
  holds: "Holds",
  past: "Past Shows",
};

/** The filter each tab sends — upcoming tabs sort ascending (soonest first); Past sorts descending.
 *
 * The boundary is DEVICE-LOCAL (`todayIso`), never UTC: a UTC "today" rolls over at 19:00 CDT / 17:00 PDT, so
 * `from=` would drop tonight's show from Upcoming while the band is loading in. A show ON today stays
 * "current" until tomorrow, so Past is `date < today`. */
function filtersFor(tab: BookingTab): ShowFilters {
  switch (tab) {
    case "confirmed":
      return { status: "confirmed", from: todayIso(), sort: "date" };
    case "holds":
      return { status: "hold", from: todayIso(), sort: "date" };
    case "past":
      return { to: todayIso(-1), sort: "-date" };
  }
}

/** The off/travel days worth reading for this tab — the same window the shows query uses. */
function tourDayWindow(tab: BookingTab): { from?: string; to?: string } {
  return tab === "past" ? { to: todayIso(-1) } : { from: todayIso() };
}

/**
 * The Booking tab, laid out to the module spec's four regions in order: the financials strip, ONE shows card
 * carrying the tab pills and the split "+ Add show" in its own header, a 2-up Book-a-Show ∥ Quote-Builder
 * grid, and the dark Venue CNTRL banner.
 *
 * There is **no visible page title** — the spec's canvas opens on the stat strip and the card title
 * (Upcoming Shows / Holds / Past Shows) is the heading. The `h1` below is sr-only so the document outline and
 * the app-wide h1-per-screen convention survive, exactly as `ContactsScreen` does it.
 *
 * MONEY-FREE by construction: the 4 KPI cards are a separate, self-gating money component and no row here
 * carries a figure.
 */
export function BookingScreen({ onAddShow, onOpenShow, onEditShow, tab, onTabChange }: BookingScreenProps) {
  const setTab = onTabChange;
  const [showTourBuilder, setShowTourBuilder] = useState(false);
  /** `null` = closed; `{ day: null }` = create; `{ day }` = edit that off/travel day. */
  const [offDay, setOffDay] = useState<{ day: TourDayDetail | null } | null>(null);
  /** The host show id of the read-only shared show being viewed, or null. */
  const [openSharedShowId, setOpenSharedShowId] = useState<string | null>(null);
  const [bitExport, setBitExport] = useState(false);

  const shows = useShows(filtersFor(tab));
  const tours = useTours();
  const dayWindow = tourDayWindow(tab);
  const tourDays = useTourDays(dayWindow.from, dayWindow.to);
  const canWrite = useCapability("schedule:write");

  // The money for the SAME window, from the separate money-gated list. A seat with neither capability never
  // mounts the query, so their Booking page stays money-free end to end — the rows render identically, just
  // without figures. `money:per_show` is included because the row figures are per-show, not an aggregate;
  // the lifetime gate stays on the KPI strip where the aggregate lives.
  const canFullMoney = useCapability("money:full");
  const canPerShowMoney = useCapability("money:per_show");
  const canSeeRowMoney = canFullMoney || canPerShowMoney;
  const moneyFilters = filtersFor(tab);
  const showMoney = useShowMoney(
    { status: moneyFilters.status, from: moneyFilters.from, to: moneyFilters.to, limit: 200 },
    canSeeRowMoney,
  );
  const money = useMemo(
    () => (canSeeRowMoney ? showMoneyById(showMoney.data) : undefined),
    [canSeeRowMoney, showMoney.data],
  );

  // The roster, indexed by person id, for the rows' member faces. ONE query for the page: `ShowSummary`
  // carries person ids only, so the names and photos are joined here against data the shell already needs
  // rather than re-fetched per row.
  const team = useBandTeam();
  const teamById = useMemo(() => {
    const map = new Map<string, { displayName?: string | null; avatarMediaId?: string | null }>();
    for (const m of team.data?.items ?? []) {
      if (m.personId) map.set(m.personId, { displayName: m.displayName, avatarMediaId: m.avatarMediaId });
    }
    return map;
  }, [team.data]);

  const tourNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tours.data?.items ?? []) if (t.tourId) map.set(t.tourId, t.name ?? "Tour");
    return map;
  }, [tours.data]);

  // showId → {date, venue}, so an off/travel row can name the show it is LINKED to (ART-240). Resolved from
  // the shows this tab already loaded — the day carries only the linked show's id, and if that show is not in
  // the window the "Linked to" line simply doesn't render.
  const showsById = useMemo(() => {
    const map = new Map<string, { date?: string; venueName?: string | null }>();
    for (const s of shows.data?.items ?? []) if (s.showId) map.set(s.showId, { date: s.date, venueName: s.venueName });
    return map;
  }, [shows.data]);

  // Accepted shared shows fold into the Upcoming list as ordinary rows (ART-165) — no separate box. Pending
  // invites are answered by `PendingInvitesGroup` above; the accepted ones read like the band's own shows.
  const sharedShows = useSharedShows();
  const acceptedShared = useMemo(
    () => (sharedShows.data ?? []).filter((s) => s.status === "accepted"),
    [sharedShows.data],
  );

  const items = useMemo(
    () =>
      buildTimeline({
        shows: shows.data?.items ?? [],
        tourDays: tourDays.data?.items ?? [],
        tourNames,
        tab,
        todayIso: todayIso(),
        sharedShows: acceptedShared,
      }),
    [shows.data, tourDays.data, tourNames, tab, acceptedShared],
  );

  // (The caret menu's dismiss/focus behaviour now lives in `SplitButton`.)


  const loading = shows.isLoading || tourDays.isLoading;

  return (
    <Page>
      {/* The route's top-level heading. The spec's layout has no page title (the shows card owns the visible
          heading), so this is visually hidden — outline + h1-per-screen convention at zero pixel cost. It is
          why this uses `<Page>` with no `title` rather than `<Page title="Booking">`. */}
      <h1 className={styles.srOnly}>Booking</h1>

      {/* Region 1 — the financials strip. Self-gates on money:lifetime, so a per_show TM / Label / Band
          Member never mounts it. Money is a SEPARATE component, never conditional fields. */}
      <BookingKpiStrip />

      {/* Region 2 — the shows card. Tabs and the split add button live in ITS header, not the page's. */}
      <Card
        id="shows"
        flush
        className={styles.showsCard}
        title={CARD_TITLE[tab]}
        actions={
          <div className={styles.tools}>
            <div className={styles.pillTabs} role="group" aria-label="Shows filter">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={styles.pill}
                  aria-pressed={tab === t.key}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {/* ART-67 (Ryan): *"sync merch only need to show on past"*. PAST ONLY, and the tab is the whole
                gate rather than a filter on a list — a future show has no sales to pull, and a show inside the
                automatic sweep's 7-day lookback is already the sweep's job. What is left for a person to press
                is the BACKFILL: the shows that fell outside that window (a store connected after a tour, a POS
                outage longer than a week, a show whose timezone landed late). It self-gates on `money:merch`,
                the same token as the merch rollup, because everything it produces is a merch figure. */}
            {tab === "past" && <SyncMerchButton />}
            {/* ART-128 (Ryan): the per-show Bandsintown buttons became ONE bulk export — pick shows, download
                Bandsintown's upload CSV, drop it on their Events › Bulk Upload. Confirmed tab only: a hold is not
                a public date, and the export lists confirmed shows alone. */}
            {canWrite && tab === "confirmed" && (
              <Button variant="ghost" size="sm" onClick={() => setBitExport(true)}>
                Export for Bandsintown
              </Button>
            )}
            {canWrite && (
              <SplitButton
                size="sm"
                menuLabel="More scheduling options"
                onClick={onAddShow}
                actions={[
                  { label: "Add tour", icon: Route, onSelect: () => setShowTourBuilder(true) },
                  { label: "Off / travel day", icon: CalendarOff, onSelect: () => setOffDay({ day: null }) },
                ]}
              >
                <Icon icon={Plus} size={16} /> Add show
              </SplitButton>
            )}
          </div>
        }
      >
        {/* Pending share invites sit at the TOP of the upcoming tabs as their own Accept/Deny group (Ryan,
            ART-165), bucketed by the HOST show's status: an invite to a confirmed date on Confirmed, one to a
            hold on Holds — the same rule the accepted rows follow. Self-hides when there is nothing to answer
            on this tab. Renders above the timeline's loading/empty branches on purpose: an invite is worth
            answering even before (or without) any show of your own on this tab. */}
        {(tab === "confirmed" || tab === "holds") && <PendingInvitesGroup tab={tab} />}
        {loading ? (
          // A skeleton, never an empty state: painting "No confirmed shows yet" before data lands reads as
          // data loss.
          <div className={styles.skeletons} data-testid="shows-loading">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="block" height={56} />
            ))}
          </div>
        ) : shows.isError ? (
          <div className={styles.listError}>
            <EmptyState
              title="Couldn't load shows"
              description={shows.error instanceof ApiError ? shows.error.message : "Please try again."}
              action={<Button onClick={() => void shows.refetch()}>Retry</Button>}
            />
          </div>
        ) : items.length === 0 ? (
          <CardEmpty>
            {tab === "confirmed" ? (
              <>
                No confirmed shows yet, add one with <CardEmptyAction>+ Add show</CardEmptyAction>.
              </>
            ) : tab === "holds" ? (
              "No holds on the books."
            ) : (
              "No past shows yet."
            )}
          </CardEmpty>
        ) : (
          <BookingTimeline
            items={items}
            onOpenShow={onOpenShow}
            /* Accepted friend-band shares render as ordinary rows and open the read-only shared detail. */
            onOpenShared={setOpenSharedShowId}
            /* Holds only: the row action is Edit, not View (TASK-057). Confirmed and Past keep the preview —
               a booked or played date is a record to read; a hold is a negotiation to change. */
            onEditShow={tab === "holds" && canWrite ? onEditShow : undefined}
            /* The Total breakdown's "Edit in show editor →" always opens the EDITOR (even on Confirmed rows,
               whose row action is View) — for a seat that can write. */
            onEditInEditor={canWrite ? onEditShow : undefined}
            onEditOffDay={(day) => setOffDay({ day })}
            /* Past is a year accordion — newest year first and open. */
            byYear={tab === "past"}
            /* The money-gated figures for this window, or undefined for a money-free seat. */
            money={money}
            /* The roster, resolved ONCE here — the rows' member faces read names and photos out of it, so a
               page of shows costs one team query rather than one per row. */
            team={teamById}
            /* showId → {date, venue} for an off/travel row's "Linked to {show}" line. */
            showsById={showsById}
          />
        )}
      </Card>

      {/* ART-241 — the Outstanding Payments card (money group only, self-gating). Full-width in the shows
          column for now (R1); the right-column pairing with the Quote Builder is tracked separately. */}
      <OutstandingPaymentsCard />

      {/* Region 3 (Book a Show) was removed entirely (Ryan, ART-210 2026-09-21 — "just kill this book a show
          box for now"). It was an inert coming-soon card; the working finder returns with Venue CNTRL. Its old
          component was deleted with the Bandsintown integration (2026-10-01). */}

      {/* Region 4 — the dark Venue CNTRL banner, one centred line (Ryan, ART-210). The badge is the actual
          Venue CNTRL wordmark (Ryan, 2026-09-21 — "this is missing the actual VC logo"), ported from the VC
          brand kit and rendered white on the dark hero, not a mono text pill. */}
      <DarkHero className={styles.soon} titleId="booking-soon-heading">
        <p className={styles.soonLine} id="booking-soon-heading">
          More booking features land soon with{" "}
          <VenueCntrlWordmark size={16} className={styles.soonLogo} />
        </p>
      </DarkHero>

      {/* An accepted friend-band share opens the guest's read-only VIEW modal first (ART-165); a schedule:write
          member can then Edit into the guest editor. The inviter's basics/lineup/run-of-show render read-only,
          the guest's own sections editable. */}
      {openSharedShowId && (
        <GuestShowModal showId={openSharedShowId} onClose={() => setOpenSharedShowId(null)} />
      )}

      {bitExport && <BandsintownExportModal onClose={() => setBitExport(false)} />}
      {offDay && (
        <OffDayModal existing={offDay.day} onClose={() => setOffDay(null)} onSaved={() => setOffDay(null)} />
      )}

      {canWrite && showTourBuilder && (
        <Modal
          size="lg"
          title="Create a tour"
          onClose={() => setShowTourBuilder(false)}
        >
          <TourBuilder onClose={() => setShowTourBuilder(false)} />
        </Modal>
      )}
    </Page>
  );
}
